import type { Asset } from '@passkeys/assets';
import { useEffect, useRef } from 'react';

import exodus from '@/ui/exodus';
import useAssets from '@/ui/hooks/use-assets';
import useBalances from '@/ui/hooks/use-balances';
import useExpandedEffect from '@/ui/hooks/use-expanded-effect';
import useIsCollapsed from '@/ui/hooks/use-is-collapsed';
import useUsdBalanceByAsset from '@/ui/hooks/use-usd-balance-by-asset';

const includeAssetBalances = [
  { name: 'bitcoin' },
  { name: 'ethereum' },
  { name: 'solana' },
  { name: 'polygon' },
  { name: 'binance' },
  { name: 'tetherusd_solana', alias: 'usdtSolana' },
  { name: 'usdcoin_solana', alias: 'usdcSolana' },
];

function useTrackAssetView(asset?: Asset) {
  const { value: balances, loading: loadingBalances } = useBalances();
  const assets = useAssets();
  const usdBalanceByAsset = useUsdBalanceByAsset();
  const tracked = useRef<boolean>(false);

  const isCollapsed = useIsCollapsed();

  useEffect(() => {
    if (isCollapsed) {
      tracked.current = false;
    }
  }, [isCollapsed]);

  useExpandedEffect(() => {
    if (!asset || loadingBalances || !usdBalanceByAsset || tracked.current) {
      return;
    }

    tracked.current = true;

    const balanceProps = {
      assetBalance: balances[asset.name].balance.toDefaultString(),
      assetBalanceUsd: usdBalanceByAsset[asset.name].toDefaultString(),
      spendableBalance: balances[asset.name].spendableBalance.toDefaultString(),
    };

    for (const { name: assetName, alias } of includeAssetBalances) {
      const ticker = alias ?? assets[assetName].displayTicker.toLowerCase();
      const { balance } = balances[assetName];
      const usd = usdBalanceByAsset[assetName];

      balanceProps[`${ticker}Balance`] = balance.toDefaultString();
      balanceProps[`${ticker}BalanceUsd`] = usd.toDefaultString();
    }

    exodus.analytics.track({
      event: 'WalletAssetView',
      properties: {
        ...balanceProps,
        assetName: asset.name,
        network: asset.baseAssetName,
        priceOrBalance: 'balance',
      },
    });
  }, [balances, loadingBalances, usdBalanceByAsset, asset]);
}

export default useTrackAssetView;
